import type { LocationData } from '@/app/_shared/store/type';
import { useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { toZonedTime } from 'date-fns-tz';
import { appDeviceAtom } from '@/app/_shared/store/device';
import { SESSION_KEY } from '@/app/_components/feature/plp-srp-filter/filter-storage';
import { setCookieLocation } from '@/app/(pages)/results/utils/set-cookie-location';
import { isLocationAgreedAtom } from '@/app/_shared/store//member';
import { localLocationAtom } from '@/app/_shared/store/location';
import { getPageKey } from '@/app/(pages)/results/utils/get-page-key';

interface Coordinates {
  latitude: number;
  longitude: number;
}

type LocationStorageData = Record<string, Partial<LocationData>>;

const LOCATION_VALIDITY_DURATION = 10 * 60 * 1000; // 10분

export const getLocationStorageData = (): LocationStorageData => {
  if (typeof window === 'undefined') return {};
  const data = sessionStorage.getItem(SESSION_KEY.localLocation);
  return data ? JSON.parse(data) : {};
};

export const updateLocationStorage = (pageKey: string, locationData: Partial<LocationData>): void => {
  if (typeof window === 'undefined') return;
  const existingData = getLocationStorageData();
  sessionStorage.setItem(
    SESSION_KEY.localLocation,
    JSON.stringify({
      ...existingData,
      [pageKey]: locationData,
    }),
  );
};

export const getCurrentPosition = (): Promise<Coordinates> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        resolve({ latitude, longitude });
      },
      (error) => {
        let errorMessage = '알 수 없는 에러가 발생했습니다.';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = '사용자가 위치 정보 제공을 거부했습니다.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = '위치 정보를 사용할 수 없습니다.';
            break;
          case error.TIMEOUT:
            errorMessage = '위치 정보 요청 시간이 초과되었습니다.';
            break;
        }
        reject(new Error(errorMessage));
      },
      {
        enableHighAccuracy: true, // 정확도
        maximumAge: 10 * 1000, // 10초
        timeout: 5 * 1000, // 5초
      },
    );
  });
};

export const useLocationManagement = () => {
  const [localLocation, setLocalLocation] = useAtom(localLocationAtom);
  const isLocationAgreed = useAtomValue(isLocationAgreedAtom);
  const appDevice = useAtomValue(appDeviceAtom);

  const handleLocationUpdate = async (pageKey: string | null) => {
    // 앱이 아닐때만 해당 로직 실행
    if (appDevice) return;

    const storageData = getLocationStorageData();

    if (!!pageKey && storageData[pageKey]) {
      // 세션 스토리지에 저장된 위치 정보가 있으면 종료
      setLocalLocation(storageData[pageKey]);
      return;
    }

    const isLocationTypeGps = localLocation?.locationType === 'GPS';
    const locationTime = localLocation?.locationTime;
    const isLocationTimeValid =
      locationTime && Date.now() - new Date(locationTime * 1000).getTime() < LOCATION_VALIDITY_DURATION;

    if (!isLocationAgreed || (isLocationTypeGps && isLocationTimeValid)) {
      // 위치 정보 동의가 안되어 있거나 / GPS 로 측위된 위치 정보가 유효하면 --> locationInfo 저장
      localLocation && pageKey && updateLocationStorage(pageKey, localLocation);
      return;
    }

    try {
      const coords = await getCurrentPosition();
      const newLocation: Partial<LocationData> = {
        latitude: String(Number(coords.latitude.toFixed(5))),
        longitude: String(Number(coords.longitude.toFixed(5))),
        locationType: 'GPS',
        locationTime: Math.floor(toZonedTime(new Date(), 'Asia/Seoul').getTime() / 1000),
        hasPermissionError: false,
      };

      setLocalLocation(newLocation);
      setCookieLocation(newLocation);

      pageKey && updateLocationStorage(pageKey, newLocation);
    } catch (error) {
      console.error('Error getting current position:', error instanceof Error ? error.message : 'Unknown error');
      localLocation && pageKey && updateLocationStorage(pageKey, localLocation);
    }
  };

  useEffect(() => {
    const pageKey = getPageKey();
    handleLocationUpdate(pageKey);
  }, []);
};
